import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, FilePlus, ClipboardList, Wallet, ReceiptText, Bell, User, Settings, HelpCircle, LogOut, History } from "lucide-react";
import Logo from "@/components/layout/Logo";
import ThemeToggle from "@/components/widgets/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const items = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/app/apply", icon: FilePlus, label: "Apply Loan" },
  { to: "/app/loans", icon: ClipboardList, label: "Loan Status" },
  { to: "/app/active", icon: Wallet, label: "Active Loans" },
  { to: "/app/history", icon: History, label: "Previous Loans" },
  { to: "/app/repayment", icon: ReceiptText, label: "Repayment" },
  { to: "/app/statements", icon: ReceiptText, label: "Statements" },
  { to: "/app/notifications", icon: Bell, label: "Notifications" },
  { to: "/app/profile", icon: User, label: "Profile" },
  { to: "/app/settings", icon: Settings, label: "Settings" },
  { to: "/app/help", icon: HelpCircle, label: "Help Centre" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      <aside className="hidden lg:flex flex-col border-r border-border/60 bg-card/40" data-testid="customer-sidebar">
        <div className="px-6 py-5 border-b border-border/60"><Logo /></div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`} data-testid={`side-${label.toLowerCase().replace(/\s+/g,"-")}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border/60 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center text-white font-display font-semibold">{(user?.name || "U")[0]}</div>
          <div className="text-sm flex-1 min-w-0">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onLogout} data-testid="logout-btn"><LogOut className="h-4 w-4" /></Button>
        </div>
      </aside>

      <div className="flex flex-col">
        <header className="h-16 border-b border-border/60 flex items-center justify-between px-6 glass sticky top-0 z-20">
          <div className="lg:hidden"><Logo compact /></div>
          <div className="hidden lg:block text-sm text-muted-foreground">Welcome back, <span className="text-foreground font-medium">{user?.name?.split(" ")[0]}</span></div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/app/notifications"><Button variant="ghost" size="icon" data-testid="topbar-notifications"><Bell className="h-4 w-4" /></Button></Link>
            <Button variant="outline" size="sm" onClick={onLogout} className="lg:hidden">Sign out</Button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">{children}</main>
      </div>
    </div>
  );
}
